/**
 * A REGRA e o roteiro da admissão. Módulo PURO.
 *
 * Duas coisas moram aqui, e por razões diferentes:
 *
 * **O checklist** é DADO tipado, e não conteúdo de banco. Pelo critério que o
 * porte do Simulador MEI fixou: texto que descreve procedimento estável não tem
 * temporada — o ASO antes do início, o S-2200 até o dia anterior, a ficha do
 * vale-transporte. Nada disso muda em janeiro. Se um dia a equipe quiser editar
 * sem deploy, o caminho é o motor de conteúdo da 000073 com um card próprio; está
 * anotado no runbook. Enquanto ninguém pedir, constante tipada é mais simples e
 * não mente sobre quem mantém o texto.
 *
 * **Os prazos** são regra de verdade, com aritmética de datas — e por isso têm
 * prova. O relógio entra por parâmetro, nunca por `new Date()` aqui dentro.
 */

export type FaseId = "pre" | "dia" | "pos";

export interface ItemChecklist {
  id: string;
  titulo: string;
  detalhe: string;
  /** Item cuja falha gera multa ou nulidade — a tela destaca. */
  critico?: boolean;
  /** Prazo próprio, quando o item tem um. */
  prazo?: string;
  base?: string;
}

export interface Fase {
  id: FaseId;
  titulo: string;
  quando: string;
  itens: ItemChecklist[];
}

export const FASES: Fase[] = [
  {
    id: "pre",
    titulo: "Antes de começar",
    quando: "Da proposta aceita até o dia anterior ao início",
    itens: [
      {
        id: "pre-proposta",
        titulo: "Proposta de emprego aceita, por escrito",
        detalhe: "Cargo, salário, jornada, benefícios e data de início. Não é o contrato — é o que evita divergência quando ele for assinado.",
      },
      {
        id: "pre-documentos",
        titulo: "Documentos do empregado recolhidos",
        detalhe: "CPF, documento de identidade, PIS/NIS, comprovante de residência, escolaridade, certidão de casamento e, para salário-família, documentos dos filhos. A CTPS é digital: não se pede a carteira física.",
        base: "CLT art. 41 e Portaria 1.195/2019",
      },
      {
        id: "pre-aso",
        titulo: "Exame admissional (ASO) realizado e arquivado",
        detalhe: "Tem de acontecer ANTES do início das atividades, e o resultado precisa ser 'apto'. Admitir sem ASO é infração autônoma e responsabiliza a empresa em caso de acidente no primeiro dia.",
        critico: true,
        prazo: "Antes do primeiro dia",
        base: "NR-7, item 7.5.1",
      },
      {
        id: "pre-conta",
        titulo: "Conta para pagamento definida",
        detalhe: "Conta salário no banco conveniado, ou portabilidade pedida pelo empregado. O empregado não é obrigado a abrir conta em banco escolhido pela empresa.",
      },
      {
        id: "pre-cadastro",
        titulo: "Cadastro na folha e no controle de ponto",
        detalhe: "Matrícula, centro de custo, escala e gestor. É aqui que a jornada contratada entra no sistema — e é o que o eSocial vai conferir depois.",
      },
    ],
  },
  {
    id: "dia",
    titulo: "No primeiro dia",
    quando: "Data efetiva de início das atividades",
    itens: [
      {
        id: "dia-s2200",
        titulo: "S-2200 transmitido ANTES do início da jornada",
        detalhe: "O evento de admissão vai até o dia imediatamente anterior ao início. Transmitir depois é registro em atraso, com multa por empregado — e o valor sobe se houver fiscalização.",
        critico: true,
        prazo: "Até o dia anterior ao início",
        base: "CLT art. 47 e Manual do eSocial",
      },
      {
        id: "dia-contrato",
        titulo: "Contrato de trabalho assinado",
        detalhe: "Duas vias, uma para cada parte. A modalidade escolhida muda cláusulas inteiras — ver a aba do contrato.",
      },
      {
        id: "dia-ctps",
        titulo: "Registro na CTPS digital conferido",
        detalhe: "O registro é alimentado pelo próprio S-2200; não há o que assinar. Confira na conta gov.br do empregado que a admissão apareceu.",
      },
      {
        id: "dia-vt",
        titulo: "Opção pelo vale-transporte, assinada",
        detalhe: "Com declaração do trajeto, ou de recusa. Sem o documento, o desconto de 6% não tem respaldo.",
        base: "Lei 7.418/85 e Decreto 95.247/87",
      },
      {
        id: "dia-salario-familia",
        titulo: "Termo do salário-família, se houver filhos",
        detalhe: "Para filhos até 14 anos, ou inválidos de qualquer idade, com remuneração dentro do limite. Exige certidão de nascimento e caderneta de vacinação em dia.",
      },
      {
        id: "dia-nr1",
        titulo: "Treinamento inicial de segurança realizado",
        detalhe: "Ordem de serviço e integração, com lista de presença assinada. É o documento que a fiscalização pede primeiro.",
        base: "NR-1, item 1.4.1",
      },
      {
        id: "dia-epi",
        titulo: "EPI entregue, com ficha de controle",
        detalhe: "Quando o cargo exige. A ficha registra qual equipamento, quando e a assinatura de quem recebeu.",
        base: "NR-6",
      },
    ],
  },
  {
    id: "pos",
    titulo: "Nos primeiros dias",
    quando: "Da primeira semana ao primeiro fechamento de folha",
    itens: [
      {
        id: "pos-beneficios",
        titulo: "Inclusão nos benefícios",
        detalhe: "Vale-refeição, plano de saúde, seguro de vida. Cada operadora tem seu corte mensal — perder o corte atrasa o benefício em um mês inteiro.",
      },
      {
        id: "pos-cct",
        titulo: "Convenção coletiva aplicada",
        detalhe: "Piso da categoria, adicionais e cláusulas específicas do sindicato. Salário abaixo do piso é diferença devida desde a admissão.",
      },
      {
        id: "pos-s2205",
        titulo: "S-2205, se algum dado cadastral estava errado",
        detalhe: "Correção depois de o S-2200 já ter sido processado. Até o dia 15 do mês seguinte.",
        prazo: "Dia 15 do mês seguinte",
      },
      {
        id: "pos-s2206",
        titulo: "S-2206, se o contrato mudou",
        detalhe: "Cargo, salário, jornada ou lotação alterados depois da admissão.",
        prazo: "Dia 15 do mês seguinte",
      },
      {
        id: "pos-primeira-folha",
        titulo: "Primeira folha com o proporcional dos dias",
        detalhe: "Quem entra no meio do mês recebe os dias trabalhados, e o S-1200 acompanha. Confira o proporcional do vale-transporte e dos benefícios.",
      },
      {
        id: "pos-fgts",
        titulo: "FGTS do mês de admissão depositado",
        detalhe: "Pelo FGTS Digital, até o dia 20 do mês seguinte.",
        prazo: "Dia 20 do mês seguinte",
        base: "Lei 8.036/90 art. 15",
      },
      {
        id: "pos-experiencia",
        titulo: "Fim do contrato de experiência agendado",
        detalhe: "Se houver experiência, a data do fim precisa estar no calendário de quem decide. Deixar passar transforma o contrato em prazo indeterminado — ver a aba do contrato.",
        critico: true,
      },
    ],
  },
];

/** Total de itens do roteiro, derivado — nunca digitado ao lado. */
export const TOTAL_ITENS = FASES.reduce((n, f) => n + f.itens.length, 0);

/** Todos os ids, para a tela validar o que veio do armazenamento local. */
export const IDS_VALIDOS = new Set(FASES.flatMap((f) => f.itens.map((i) => i.id)));

export interface ProgressoChecklist {
  concluidos: number;
  total: number;
  percentual: number;
  /** Itens críticos que ainda faltam — é o que a tela destaca. */
  criticosPendentes: ItemChecklist[];
}

/**
 * O progresso do roteiro.
 *
 * Só conta id que EXISTE no roteiro. Sem esse filtro, um id antigo guardado no
 * navegador — de quando um item se chamava outra coisa — contaria como concluído
 * para sempre, e o aluno veria "18 de 18" com item nunca marcado. É a mesma
 * família da validação de entrada da 000075: perguntar à entrada, antes.
 */
export function progresso(marcados: Record<string, boolean>): ProgressoChecklist {
  const concluidos = Object.entries(marcados).filter(([id, v]) => v && IDS_VALIDOS.has(id)).length;
  const criticosPendentes = FASES.flatMap((f) => f.itens).filter(
    (i) => i.critico && !marcados[i.id],
  );
  return {
    concluidos,
    total: TOTAL_ITENS,
    percentual: TOTAL_ITENS > 0 ? Math.round((concluidos / TOTAL_ITENS) * 100) : 0,
    criticosPendentes,
  };
}

// ---------------------------------------------------------------------------
// Modalidades de contrato
// ---------------------------------------------------------------------------

export type Modalidade = "indeterminado" | "experiencia" | "determinado" | "intermitente" | "aprendiz";

export interface DefinicaoModalidade {
  rotulo: string;
  base: string;
  /** Duração máxima em dias, quando há. */
  duracaoMaximaDias: number | null;
  /** Quantas prorrogações a lei admite. */
  prorrogacoes: number;
  resumo: string;
  atencao: string;
}

export const MODALIDADES: Record<Modalidade, DefinicaoModalidade> = {
  indeterminado: {
    rotulo: "Prazo indeterminado",
    base: "CLT art. 443",
    duracaoMaximaDias: null,
    prorrogacoes: 0,
    resumo: "O contrato padrão. Sem data de fim; a rescisão segue as regras do art. 477.",
    atencao: "Dispensa sem justa causa gera aviso prévio, multa de 40% do FGTS e seguro-desemprego.",
  },
  experiencia: {
    rotulo: "Experiência",
    base: "CLT art. 443, §2º, c e art. 445, parágrafo único",
    duracaoMaximaDias: 90,
    prorrogacoes: 1,
    resumo: "No máximo 90 dias no total, com UMA única prorrogação dentro desse limite.",
    atencao:
      "Passar de 90 dias, ou prorrogar duas vezes, converte o contrato em prazo INDETERMINADO — e a rescisão passa a custar aviso prévio e multa de 40%.",
  },
  determinado: {
    rotulo: "Prazo determinado",
    base: "CLT art. 443, §2º e art. 445",
    duracaoMaximaDias: 730,
    prorrogacoes: 1,
    resumo: "Até 2 anos, com uma prorrogação. Só nas hipóteses do §2º: serviço transitório, atividade empresarial transitória ou contrato de experiência.",
    atencao: "Rescindir antes do fim gera indenização de metade do que faltava (art. 479). Não há multa de 40% se o contrato chegar ao termo.",
  },
  intermitente: {
    rotulo: "Intermitente",
    base: "CLT art. 452-A (Reforma de 2017)",
    duracaoMaximaDias: null,
    prorrogacoes: 0,
    resumo: "Prestação não contínua, com convocação por escrito e no mínimo 3 dias de antecedência. O empregado pode recusar.",
    atencao:
      "Cada período de convocação gera férias, 13º e FGTS proporcionais, pagos ao fim de cada prestação. Ficar sem convocação por um ano encerra o contrato.",
  },
  aprendiz: {
    rotulo: "Aprendiz",
    base: "CLT art. 428 e Decreto 9.579/2018",
    duracaoMaximaDias: 730,
    prorrogacoes: 0,
    resumo: "De 14 a 24 anos (sem limite de idade para pessoa com deficiência), com matrícula em curso de aprendizagem e jornada de até 6 horas.",
    atencao: "FGTS de 2%, não 8%. Jornada de 8h só para quem concluiu o ensino médio e se incluir as horas do curso.",
  },
};

// ---------------------------------------------------------------------------
// Prazos
// ---------------------------------------------------------------------------

/** Dias somados a uma data, sem tocar no original. */
export function somarDias(data: Date, dias: number): Date {
  return new Date(data.getFullYear(), data.getMonth(), data.getDate() + dias);
}

export interface PrazosDaAdmissao {
  inicio: Date;
  /** Limite do S-2200: o dia anterior ao início. */
  limiteS2200: Date;
  /** Fim da experiência, quando houver. */
  fimExperiencia: Date | null;
  /** Fim se a experiência for prorrogada até o máximo legal. */
  fimExperienciaProrrogada: Date | null;
  /** Dias que sobram para a prorrogação caber nos 90. */
  diasDisponiveisProrrogacao: number;
  /** True quando o primeiro período já usa os 90 dias e não cabe prorrogar. */
  semEspacoParaProrrogar: boolean;
  /** True quando o período pedido já é ilegal por si só. */
  excedeLimiteLegal: boolean;
}

/**
 * Os prazos que dependem da data de início.
 *
 * **A experiência tem limite de 90 dias no TOTAL**, somando o período inicial e a
 * prorrogação — não 90 + 90. É o erro que converte o contrato em prazo
 * indeterminado sem ninguém perceber, e a consequência aparece só na rescisão,
 * quando a empresa descobre que deve aviso prévio e 40% de multa.
 *
 * O primeiro dia de trabalho CONTA no período: um contrato de 45 dias que começa
 * no dia 1º termina no dia 45, não no 46. É por isso que o cálculo é
 * `inicio + (dias − 1)`.
 */
export function prazosDaAdmissao(inicio: Date, diasExperiencia: number): PrazosDaAdmissao {
  const maximo = MODALIDADES.experiencia.duracaoMaximaDias ?? 90;
  const pedidos = Math.max(0, Math.round(diasExperiencia));
  const temExperiencia = pedidos > 0;
  const disponiveis = Math.max(0, maximo - pedidos);

  return {
    inicio,
    limiteS2200: somarDias(inicio, -1),
    fimExperiencia: temExperiencia ? somarDias(inicio, pedidos - 1) : null,
    fimExperienciaProrrogada:
      temExperiencia && disponiveis > 0 ? somarDias(inicio, maximo - 1) : null,
    diasDisponiveisProrrogacao: disponiveis,
    semEspacoParaProrrogar: temExperiencia && disponiveis === 0,
    excedeLimiteLegal: pedidos > maximo,
  };
}
